import {
    Chart as ChartJS,
    LinearScale,
    LogarithmicScale,
    PointElement,
    Tooltip,
    Legend,
    type ChartOptions,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Bubble } from 'react-chartjs-2';
import type { CSSProperties, RefObject } from 'react';

ChartJS.register(LinearScale, LogarithmicScale, PointElement, Tooltip, Legend, annotationPlugin);

export const QUADRANT_COLORS: Record<string, string> = {
    Powerhouse: '#2e7d32',
    Overachievers: '#1565c0',
    'Sleeping Giants': '#f0a202',
    'Question Mark': '#98a2b3',
    'Core Powerhouse': '#2e7d32',
    'Elite But Limited': '#1565c0',
    'Safety Net': '#f0a202',
    'Dead Zone': '#98a2b3',
};

/** Label → region relative to thresholds (highX / highY). */
export const AHLI_QUAD_META: { key: string; label: string; highX: boolean; highY: boolean }[] = [
    { key: 'Powerhouse', label: 'POWERHOUSE', highX: true, highY: true },
    { key: 'Overachievers', label: 'OVERACHIEVER', highX: false, highY: true },
    { key: 'Sleeping Giants', label: 'SLEEPING GIANT', highX: true, highY: false },
    { key: 'Question Mark', label: 'QUESTION MARK', highX: false, highY: false },
];

export const SPM_QUAD_META: { key: string; label: string; highX: boolean; highY: boolean }[] = [
    { key: 'Core Powerhouse', label: 'CORE POWERHOUSE', highX: true, highY: true },
    { key: 'Elite But Limited', label: 'ELITE BUT LIMITED', highX: false, highY: true },
    { key: 'Safety Net', label: 'SAFETY NET', highX: true, highY: false },
    { key: 'Dead Zone', label: 'DEAD ZONE', highX: false, highY: false },
];

export function bubbleSize(alumniCount: number | null | undefined): number {
    const n = Number(alumniCount) || 1;
    return Math.max(4, Math.min(28, Math.sqrt(n) * 2.2));
}

export function quadrantFromPoint(
    x: number,
    y: number,
    xThreshold: number,
    yThreshold: number,
    meta: { key: string; highX: boolean; highY: boolean }[]
): string | null {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    const highX = x >= xThreshold;
    const highY = y >= yThreshold;
    const hit = meta.find((m) => m.highX === highX && m.highY === highY);
    return hit?.key ?? null;
}

/**
 * Keep all bubbles; dim non-selected when a quadrant is active, and/or dim non-matching
 * companies when a search filter is active (prototype highlight). A bubble is "highlighted"
 * (shown in its quadrant colour) only if it passes BOTH the quadrant filter (if any) and the
 * search filter (if any); all other bubbles are greyed out.
 */
export function buildBubbleData(
    bubbles: any[],
    selectedQuadrant: string | null,
    xKey = 'x',
    yKey = 'y',
    matchedCompanyIds?: Set<string | number> | null
) {
    const hasFilter = !!selectedQuadrant || !!matchedCompanyIds;
    const isHighlighted = (b: any) => {
        if (selectedQuadrant && b.quadrant !== selectedQuadrant) return false;
        if (matchedCompanyIds && !matchedCompanyIds.has(b.companyId)) return false;
        return true;
    };
    return {
        datasets: [
            {
                label: 'Companies',
                data: bubbles.map((b) => ({
                    x: Number(b[xKey]),
                    y: Number(b[yKey]),
                    r: bubbleSize(b.bubbleSize ?? b.alumniCount),
                    companyName: b.companyName,
                    companyId: b.companyId,
                    quadrant: b.quadrant,
                    raw: b,
                })),
                backgroundColor: bubbles.map((b) => {
                    const c = QUADRANT_COLORS[b.quadrant] || '#607d8b';
                    if (!hasFilter) return c + '99';
                    return isHighlighted(b) ? c : c + '22';
                }),
                borderColor: bubbles.map((b) => {
                    const c = QUADRANT_COLORS[b.quadrant] || '#607d8b';
                    if (!hasFilter) return c;
                    return isHighlighted(b) ? c : c + '33';
                }),
                borderWidth: bubbles.map((b) => (hasFilter && isHighlighted(b) ? 2 : 1)),
            },
        ],
    };
}

/**
 * Split bubbles into main-plot bubbles, X-axis outliers (cappedAtXMax), and Y-axis
 * outliers (cappedAtYMax). A bubble capped on both axes is treated as an X outlier.
 */
export function splitOutliers(bubbles: any[]): { main: any[]; xOutliers: any[]; yOutliers: any[] } {
    const main: any[] = [];
    const xOutliers: any[] = [];
    const yOutliers: any[] = [];
    for (const b of bubbles) {
        if (b?.cappedAtXMax) xOutliers.push(b);
        else if (b?.cappedAtYMax) yOutliers.push(b);
        else main.push(b);
    }
    return { main, xOutliers, yOutliers };
}

/** Y-axis domain for outlier lane — must match main plot (configured yMax, else nice max of all bubbles). */
export function plotYDomainMax(bubbles: any[], configuredYMax?: number): number {
    const configured = Number(configuredYMax);
    if (Number.isFinite(configured) && configured > 0) return configured;
    let max = 0;
    for (const b of bubbles || []) {
        const v = Number(b?.yRaw ?? b?.y);
        if (Number.isFinite(v) && v > max) max = v;
    }
    if (max <= 0) return 1;
    // Match Chart.js-ish nice ceiling (steps of 5 when in typical salary/HD ranges)
    if (max <= 50) return Math.ceil(max / 5) * 5;
    if (max <= 100) return Math.ceil(max / 10) * 10;
    return Math.ceil(max / 20) * 20;
}

/**
 * Narrow lane to the right of the matrix plot for companies whose X value exceeds
 * the configured axis max. Dots are placed by Y (Hiring Depth / Salary) so they
 * line up with the main plot's vertical scale.
 */
export function OutlierLane({
    bubbles,
    height,
    label = 'Outliers',
    yMax,
    onHoverPoint,
    containerRef,
}: {
    bubbles: any[];
    height: number;
    label?: string;
    /** Matches the main plot's y-axis max so dots align with Hiring Depth / Salary. */
    yMax?: number;
    onHoverPoint?: (
        payload: { bubble: any; x: number; y: number; chartW: number; chartH: number } | null
    ) => void;
    containerRef?: RefObject<HTMLDivElement>;
}) {
    if (!bubbles.length) return null;
    const labelH = 22;
    // Chart.js reserves space for x-axis ticks (~18px) above the axis title;
    // keep the same inset so Y=0 lines up with the plot baseline, not the tick row.
    const chartBottomPad = 18;
    const chartTopPad = 8;
    const dotsH = Math.max(0, height - labelH);
    const plotH = Math.max(0, dotsH - chartBottomPad - chartTopPad);
    const sorted = [...bubbles].sort(
        (a, b) => Number(b.yRaw ?? b.y ?? 0) - Number(a.yRaw ?? a.y ?? 0)
    );
    const domainMax = yMax && yMax > 0 ? yMax : Math.max(...sorted.map((b) => Number(b.yRaw ?? b.y ?? 0)), 1);

    return (
        <div className="mx-outlier-lane" style={{ height }}>
            <div className="mx-outlier-dots" style={{ height: dotsH }}>
                {sorted.map((b, i) => {
                    const size = bubbleSize(b.bubbleSize ?? b.alumniCount);
                    const color = QUADRANT_COLORS[b.quadrant] || '#607d8b';
                    const yVal = Number(b.yRaw ?? b.y ?? 0);
                    const pct = Math.min(1, Math.max(0, yVal / domainMax));
                    // Chart Y grows upward: yMax at top, 0 at bottom of plot area
                    const top = chartTopPad + (1 - pct) * plotH - size / 2;
                    return (
                        <span
                            key={b.companyId ?? i}
                            className="mx-outlier-dot"
                            style={{
                                width: size,
                                height: size,
                                top,
                                background: color,
                                borderColor: color,
                            }}
                            onMouseEnter={(e) => {
                                if (!onHoverPoint) return;
                                const dotRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                const contRect = containerRef?.current?.getBoundingClientRect();
                                const x = contRect ? dotRect.left + dotRect.width / 2 - contRect.left : 0;
                                const y = contRect ? dotRect.top + dotRect.height / 2 - contRect.top : 0;
                                onHoverPoint({
                                    bubble: b,
                                    x,
                                    y,
                                    chartW: containerRef?.current?.clientWidth || 400,
                                    chartH: containerRef?.current?.clientHeight || height,
                                });
                            }}
                            onMouseLeave={() => onHoverPoint && onHoverPoint(null)}
                        />
                    );
                })}
            </div>
            <div className="mx-outlier-label">{label}</div>
        </div>
    );
}

/**
 * Horizontal lane above the matrix plot for companies whose Y value exceeds the
 * configured axis max. Mirrors `OutlierLane` but laid out left-to-right, with an
 * "Outliers" label to the left, separated from the dots by a divider.
 */
export function YOutlierLane({
    bubbles,
    height,
    label = 'Outliers',
    xMax,
    xMin,
    logX,
    onHoverPoint,
    containerRef,
}: {
    bubbles: any[];
    height: number;
    label?: string;
    /** Matches the main plot's x-axis max, so dots line up with the Influence/Offers axis below. */
    xMax?: number;
    /** Log X charts use a positive min (SPM starts at 1.0). */
    xMin?: number;
    logX?: boolean;
    onHoverPoint?: (
        payload: { bubble: any; x: number; y: number; chartW: number; chartH: number } | null
    ) => void;
    containerRef?: RefObject<HTMLDivElement>;
}) {
    if (!bubbles.length) return null;
    const sorted = [...bubbles].sort((a, b) => Number(b.yRaw ?? b.y ?? 0) - Number(a.yRaw ?? a.y ?? 0));
    const domainMax =
        xMax && xMax > 0
            ? xMax
            : Math.max(...sorted.map((b) => Number(b.xRaw ?? b.x ?? 0)), logX ? 10 : 1);
    const domainMin = logX ? (xMin && xMin > 0 ? xMin : 1) : 0;

    const pctForValue = (val: number) => {
        if (logX) {
            const safeMin = domainMin > 0 ? domainMin : 1;
            const safeMax = Math.max(domainMax, safeMin * 10);
            const safeVal = Math.max(val, safeMin);
            const lo = Math.log10(safeMin);
            const hi = Math.log10(safeMax);
            if (hi <= lo) return 0;
            return Math.min(100, Math.max(0, ((Math.log10(safeVal) - lo) / (hi - lo)) * 100));
        }
        if (domainMax <= 0) return 0;
        return Math.min(100, Math.max(0, (val / domainMax) * 100));
    };

    return (
        <div className="mx-outlier-lane-top" style={{ height }}>
            <div className="mx-outlier-label-vert">{label}</div>
            <div className="mx-outlier-dots-row">
                {sorted.map((b, i) => {
                    const size = bubbleSize(b.bubbleSize ?? b.alumniCount);
                    const color = QUADRANT_COLORS[b.quadrant] || '#607d8b';
                    const pct = pctForValue(Number(b.xRaw ?? b.x ?? 0));
                    return (
                        <span
                            key={b.companyId ?? i}
                            className="mx-outlier-dot-h"
                            style={{
                                width: size,
                                height: size,
                                left: `${pct}%`,
                                background: color,
                                borderColor: color,
                            }}
                            onMouseEnter={(e) => {
                                if (!onHoverPoint) return;
                                const dotRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                const contRect = containerRef?.current?.getBoundingClientRect();
                                const x = contRect ? dotRect.left + dotRect.width / 2 - contRect.left : 0;
                                const y = contRect ? dotRect.top + dotRect.height / 2 - contRect.top : 0;
                                onHoverPoint({
                                    bubble: b,
                                    x,
                                    y,
                                    chartW: containerRef?.current?.clientWidth || 400,
                                    chartH: containerRef?.current?.clientHeight || height,
                                });
                            }}
                            onMouseLeave={() => onHoverPoint && onHoverPoint(null)}
                        />
                    );
                })}
            </div>
        </div>
    );
}

export function matrixOptions(opts: {
    xLabel: string;
    yLabel: string;
    xThreshold: number;
    yThreshold: number;
    xMax?: number;
    yMax?: number;
    logX?: boolean;
    selectedQuadrant?: string | null;
    quadrantMeta?: { key: string; label: string; highX: boolean; highY: boolean }[];
    onHoverPoint?: (
        payload: { bubble: any; x: number; y: number; chartW: number; chartH: number } | null
    ) => void;
    onQuadrantSelect?: (quadrant: string | null) => void;
}): ChartOptions<'bubble'> {
    const {
        xLabel,
        yLabel,
        xThreshold,
        yThreshold,
        xMax,
        yMax,
        logX,
        selectedQuadrant,
        quadrantMeta = AHLI_QUAD_META,
        onHoverPoint,
        onQuadrantSelect,
    } = opts;

    const xPad = logX ? xThreshold * 0.01 : 0;
    const annotations: Record<string, any> = {
        xLine: {
            type: 'line',
            xMin: xThreshold,
            xMax: xThreshold,
            borderColor: '#9e9e9e',
            borderWidth: 1,
            borderDash: [4, 4],
        },
        yLine: {
            type: 'line',
            yMin: yThreshold,
            yMax: yThreshold,
            borderColor: '#9e9e9e',
            borderWidth: 1,
            borderDash: [4, 4],
        },
    };

    if (yMax && yMax > 0) {
        annotations.yMaxLine = {
            type: 'line',
            yMin: yMax,
            yMax: yMax,
            borderColor: '#c62828',
            borderWidth: 1,
            borderDash: [2, 2],
        };
    }

    if (xMax && xMax > 0) {
        annotations.xMaxLine = {
            type: 'line',
            xMin: xMax,
            xMax: xMax,
            borderColor: '#c62828',
            borderWidth: 1,
            borderDash: [2, 2],
        };
    }

    // Watermark labels + highlight fill for selected quadrant
    for (const q of quadrantMeta) {
        const active = selectedQuadrant === q.key;
        annotations[`qbox_${q.key}`] = {
            type: 'box',
            xMin: q.highX ? xThreshold + xPad : undefined,
            xMax: q.highX ? undefined : xThreshold - xPad,
            yMin: q.highY ? yThreshold : undefined,
            yMax: q.highY ? undefined : yThreshold,
            backgroundColor: active ? `${QUADRANT_COLORS[q.key] || '#90a4ae'}22` : 'transparent',
            borderWidth: 0,
            drawTime: 'beforeDatasetsDraw',
        };
        annotations[`qlabel_${q.key}`] = {
            type: 'label',
            xValue: q.highX ? (logX ? xThreshold * 3 : xThreshold * 1.35) : xThreshold * 0.35,
            yValue: q.highY ? yThreshold + (yMax && yMax > 0 ? (yMax - yThreshold) * 0.55 : yThreshold * 0.4) : yThreshold * 0.45,
            content: q.label,
            color: active ? QUADRANT_COLORS[q.key] || '#667085' : '#c0c8d4',
            font: { size: 14, weight: '700' as const },
            opacity: active ? 0.85 : 0.35,
            drawTime: 'beforeDatasetsDraw',
        };
    }

    return {
        responsive: true,
        maintainAspectRatio: false,
        onHover: (_e, elements, chart) => {
            if (!onHoverPoint) return;
            if (!elements.length) {
                onHoverPoint(null);
                return;
            }
            const el = elements[0];
            const ds = chart.data.datasets[el.datasetIndex] as any;
            const pt = ds.data[el.index];
            const meta = chart.getDatasetMeta(el.datasetIndex);
            const element = meta?.data?.[el.index] as any;
            const px = element?.x ?? (el as any).element?.x ?? 0;
            const py = element?.y ?? (el as any).element?.y ?? 0;
            onHoverPoint({
                bubble: pt?.raw || pt || null,
                x: Number(px),
                y: Number(py),
                chartW: chart.width,
                chartH: chart.height,
            });
        },
        onClick: (e, elements, chart) => {
            if (!onQuadrantSelect) return;
            if (elements.length) {
                const ds = chart.data.datasets[elements[0].datasetIndex] as any;
                const pt = ds.data[elements[0].index];
                const q = pt?.quadrant || pt?.raw?.quadrant;
                if (q) onQuadrantSelect(q);
                return;
            }
            const xScale = chart.scales.x;
            const yScale = chart.scales.y;
            if (!xScale || !yScale || (e as any).x == null || (e as any).y == null) return;
            const x = xScale.getValueForPixel((e as any).x);
            const y = yScale.getValueForPixel((e as any).y);
            const q = quadrantFromPoint(Number(x), Number(y), xThreshold, yThreshold, quadrantMeta);
            if (!q) return;
            onQuadrantSelect(q);
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                enabled: false,
            },
            annotation: { annotations },
        },
        scales: {
            x: {
                type: logX ? 'logarithmic' : 'linear',
                title: { display: true, text: xLabel },
                min: logX ? 1 : undefined,
                max: xMax && xMax > 0 ? xMax : undefined,
            },
            y: {
                type: 'linear',
                title: { display: true, text: yLabel },
                max: yMax && yMax > 0 ? yMax : undefined,
            },
        },
    };
}

export { Bubble };

function fmtNum(v: any, digits = 1) {
    if (v == null || v === '') return null;
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    return Number.isInteger(n) ? String(n) : n.toFixed(digits);
}

function influenceTier(cat?: string | null) {
    if (!cat) return null;
    const c = String(cat).toUpperCase();
    if (c.includes('HIGH')) return 'High';
    if (c.includes('MED')) return 'Medium';
    if (c.includes('LOW')) return 'Low';
    return String(cat);
}

/** Position hover card near bubble; flip if near right/bottom edge. */
export function hoverCardStyle(hover: {
    x: number;
    y: number;
    chartW: number;
    chartH: number;
}): CSSProperties {
    const cardW = 260;
    const cardH = 220;
    const pad = 12;
    let left = hover.x + pad;
    let top = hover.y - cardH / 3;
    if (left + cardW > hover.chartW - 8) left = hover.x - cardW - pad;
    if (left < 8) left = 8;
    if (top + cardH > hover.chartH - 8) top = hover.chartH - cardH - 8;
    if (top < 8) top = 8;
    return { left, top };
}

/** Prototype-style hover card for AHLI / SPM bubbles. */
export function MatrixHoverCard({ bubble }: { bubble: any }) {
    if (!bubble) return null;
    const tier = influenceTier(bubble.influenceCategory);
    const salary = bubble.meanSalaryLpa ?? bubble.yRaw;
    const fields: [string, string | null][] = [
        ['Influence', fmtNum(bubble.influenceScore ?? bubble.xRaw ?? bubble.x)],
        ['Alumni', fmtNum(bubble.alumniCount, 0)],
        ['Hiring Depth', fmtNum(bubble.hiringDepth ?? bubble.yRaw ?? bubble.y)],
        [
            'Visits',
            bubble.visits != null && bubble.visits !== ''
                ? `${bubble.visits}${Number(bubble.visits) === 1 ? ' yr' : ' yrs'}`
                : null,
        ],
        ['Offers', fmtNum(bubble.offers ?? bubble.weightedOffers, 0)],
        [
            'Salary',
            salary != null && salary !== ''
                ? `₹${fmtNum(salary)}L${bubble.cappedAtYMax ? ' 🚩' : ''}`
                : null,
        ],
        ['Loyalty', fmtNum(bubble.loyaltyScore)],
        ['Last 3yr', bubble.last3yr == null ? null : bubble.last3yr ? 'Yes' : 'No'],
    ];

    return (
        <div className="mx-hover-card">
            <div className="mx-hover-name">{bubble.companyName || '—'}</div>
            <div className="mx-hover-badges">
                {bubble.quadrant && (
                    <span
                        className="mx-hover-badge"
                        style={{ background: QUADRANT_COLORS[bubble.quadrant] || '#667085' }}>
                        {bubble.quadrant}
                    </span>
                )}
                {tier && <span className="mx-hover-badge mx-hover-tier">{tier}</span>}
            </div>
            <div className="mx-hover-fields">
                {fields
                    .filter(([, v]) => v != null && v !== '')
                    .map(([k, v]) => (
                        <div className="mx-hover-row" key={k}>
                            <span>{k}</span>
                            <strong>{v}</strong>
                        </div>
                    ))}
            </div>
        </div>
    );
}

